import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import loadingService from '../helpers/loading_service';

// Create the loading context
const LoadingContext = createContext();

// Custom hook to use the loading context
export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

// Loading provider component
export const LoadingProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');

  // Subscribe to loading service changes
  useEffect(() => {
    const unsubscribe = loadingService.subscribe((state) => {
      setIsLoading(state.isLoading);
      setLoadingMessage(state.loadingMessage);
    });

    // Initialize with current state
    const currentState = loadingService.getState();
    setIsLoading(currentState.isLoading);
    setLoadingMessage(currentState.loadingMessage);

    return unsubscribe;
  }, []);

  // Show loading with optional custom message
  const showLoading = useCallback((message = 'Loading...') => {
    loadingService.showLoading(message);
  }, []);

  // Hide loading
  const hideLoading = useCallback(() => {
    loadingService.hideLoading();
  }, []);

  // Set loading with message in one call
  const setLoading = useCallback((loading, message = 'Loading...') => {
    loadingService.setLoading(loading, message);
  }, []);

  // Context value
  const value = {
    isLoading,
    loadingMessage,
    showLoading,
    hideLoading,
    setLoading
  };

  return (
    <LoadingContext.Provider value={value}>
      {children}
    </LoadingContext.Provider>
  );
};

export default LoadingContext;
